/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* @typescript-eslint/ban-ts-ignore */

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createUserApi, { getInvitedUser } from 'api/user';
import {
  appContextSelector,
  setContext,
  setFeatures,
  setLogo,
  setOnboarding,
  loadCountries,
  isGuestContextSelector,
  setPartnershipId,
  setLanguages,
  setFeeDisplay,
  setOffsetTypesWithoutSaf,
} from 'modules/app/appSlice';
import { setPaymentMethod, setPaymentMethods, setPaymentMethodTypes, updateInvoicePaymentMethod } from 'modules/payments/paymentsSlice';
import { setSubscriptions } from 'modules/subscriptions/subscriptionSlice';
import { getRoles } from 'modules/allUsers/allUsersSlice';
import { AppThunk, RootState } from 'store';
import { getTypes, hasType } from 'utils/account-type';
import { defineAbilityFor } from 'utils/permissions/defineAbility';
import {
  loadAttributionForCustomer,
  loadAttributionForPartnership,
  loadAttributionForUser,
  loadCustomerAttributionDataForMyChooose,
  loadUserAttributionDataForWeChooose,
} from './attributionSlice';
import { clearCertificates } from './certificatesSlice';
import { saveGiftcards } from 'modules/offset/giftSlice';
import { loadProjects } from 'modules/projects/projectsSlice';
import createPaymentMethodApi from 'api/paymentMethod';
import { setMultiplier } from 'modules/orders/ordersSlice';
import { preDefinedMetadatasNames } from 'utils/metadata';

export const PORTAL_CONTEXT_KEY = 'portal-context';
export const PORTAL_SELECTED_PROFILE_KEY = 'selected-profile';

export const contextToAccountType: { [x: string]: AccountType } = {
  mychooose: 'MyChooose',
  wechooose: 'WeChooose',
  connect: 'WeChoooseCommmunity',
  'guest-mychooose': 'GuestMyChooose',
  'guest-wechoose': 'GuestWeChooose',
};

export const onboardingLocalStorageNames: { [Property in PortalContext]: string } = {
  wechooose: 'onboarding',
  mychooose: 'onboardingPersonal',
  connect: 'onboarding',
  'guest-mychooose': 'onboardingPersonalGuest',
  'guest-wechooose': 'onboardingGuest',
};

interface State extends Partial<User> {
  loading: boolean;
  currentAccount?: Account;
  currentProfile?: Profile;
}
const initialState: State = {
  loading: false,
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state: State, action: PayloadAction<User>): State => {
      return { ...state, ...action.payload };
    },
    setCustomer: (state: State, action: PayloadAction<Customer>): State => {
      state.currentAccount!.customer = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.loading = action.payload;
      return state;
    },
    setCurrentAccount: (state: State, action: PayloadAction<Account>): State => {
      state.currentAccount = action.payload;
      return state;
    },
    updateAccounts: (state: State, action: PayloadAction<Account[]>): State => {
      state.accounts = action.payload;
      return state;
    },
    setCurrentProfile: (state: State, action: PayloadAction<Profile>): State => {
      state.currentProfile = action.payload;
      return state;
    },
    updateProfile: (state: State, action: PayloadAction<Profile>): State => {
      state.currentProfile = action.payload;
      state.currentAccount!.profile = action.payload;
      const currentAccountIdx = state.accounts?.findIndex(a => a.customerId === state.currentAccount?.customerId) as number;
      state.accounts![currentAccountIdx].profile = action.payload;
      return state;
    },
    updatePaymentMethod: (state: State, action: PayloadAction<PaymentMethod>): State => {
      const paymentMethodIdx = state.currentAccount?.customer!.payment!.paymentMethods.findIndex(p => p.id === action.payload.id) as number;
      const currentAccountIdx = state.accounts?.findIndex(a => a.customerId === state.currentAccount?.customerId) as number;

      if (paymentMethodIdx !== -1 && currentAccountIdx !== -1) {
        state.currentAccount!.customer!.payment!.paymentMethods[paymentMethodIdx] = action.payload;
        if (state.accounts![currentAccountIdx].customer) {
          state.accounts![currentAccountIdx].customer!.payment!.paymentMethods[paymentMethodIdx] = action.payload;
        }
      }
      return state;
    },
    setAvailablePaymentMethods: (state: State, action: PayloadAction<PaymentMethod[]>): State => {
      const currentAccountIdx = state.accounts?.findIndex(a => a.customerId === state.currentAccount?.customerId) as number;
      state.currentAccount!.customer!.payment!.paymentMethods = action.payload;
      if (state.accounts![currentAccountIdx].customer) {
        state.accounts![currentAccountIdx].customer!.payment!.paymentMethods = action.payload;
      }
      return state;
    },
    updateDefaultPaymentMethod: (state: State, action: PayloadAction<PaymentMethod | undefined>): State => {
      const currentAccountIdx = state.accounts?.findIndex(a => a.customerId === state.currentAccount?.customerId) as number;
      if (state.currentAccount?.customer?.payment) {
        state.currentAccount!.customer!.payment!.defaultPaymentMethod = action.payload;
      }
      if (state.accounts![currentAccountIdx].customer?.payment) {
        state.accounts![currentAccountIdx].customer!.payment!.defaultPaymentMethod = action.payload;
      }
      return state;
    },
    updatePartnershipPaymentMethod: (state: State, action: PayloadAction<BillingInformation>): State => {
      state.currentAccount!.partnership!.settings!.invoice = action.payload;
      const currentAccountIdx = state.accounts?.findIndex(a => a.partnershipId === state.currentAccount?.partnershipId) as number;
      state.accounts![currentAccountIdx].partnership!.settings!.invoice = action.payload;
      return state;
    },
    clearMetadata: (state: State): State => {
      state.currentAccount!.customer = { ...state.currentAccount!.customer!, customMetadata: [] };
      return state;
    },
    clearSingleMetadata: (state: State, action: PayloadAction<string | string[]>): State => {
      let filteredMetadata = [];
      if (Array.isArray(action.payload)) {
        filteredMetadata =
          state.currentAccount!.customer?.customMetadata?.filter(m => !(action.payload as string[]).some((p: string) => p === m.internalName)) ?? [];
      } else {
        filteredMetadata = state.currentAccount!.customer?.customMetadata?.filter(m => m.internalName !== action.payload) ?? [];
      }
      state.currentAccount!.customer = { ...state.currentAccount!.customer!, customMetadata: filteredMetadata };
      return state;
    },
    setNewOwner: (state: State, action: PayloadAction<Owner>): State => {
      state.currentAccount!.customer!.owner = action.payload;
      const currentAccountIdx = state.accounts?.findIndex(a => a.customerId === state.currentAccount?.customerId) as number;
      state.accounts![currentAccountIdx].customer!.owner = action.payload;
      return state;
    },
    setEmailSubscription: (state: State, action: PayloadAction<MailNotifications>): State => {
      state.currentAccount!.mailNotifications = action.payload;
      return state;
    },
    changeCustomer: (state: State, action: PayloadAction<Customer>): State => {
      const account = state.accounts?.find(a => a.customerId === action.payload.id);
      if (!account) {
        return state;
      }
      account.customer = action.payload;
      state.currentAccount = account;
      state.accounts = state.accounts?.filter(a => a.id !== account.id);
      state.accounts = [...state.accounts!, account];
      return state;
    },
    updateCustomerAddress: (state: State, action: PayloadAction<{ id: string; customerInformation: CustomerInformation }>): State => {
      state.currentAccount!.customer!.info = action.payload.customerInformation;
      const currentAccountIdx = state.accounts?.findIndex(a => a.customerId === state.currentAccount?.customerId) as number;
      state.accounts![currentAccountIdx].customer!.info = action.payload.customerInformation;
      return state;
    },
    updateCustomerLanguage: (state: State, action: PayloadAction<{ language: string }>): State => {
      state.currentAccount!.profile!.language = action.payload.language;
      const currentAccountIdx = state.accounts?.findIndex(a => a.customerId === state.currentAccount?.customerId) as number;
      state.accounts![currentAccountIdx].profile!.language = action.payload.language;
      return state;
    },
    changePartnership: (state: State, action: PayloadAction<Partnership>): State => {
      const account = state.accounts?.find(a => a.partnershipId === action.payload.id);
      if (!account) {
        return state;
      }
      account.partnership = action.payload;
      state.currentAccount = account;
      state.accounts = state.accounts?.filter(a => a.id !== account.id);
      state.accounts = [...state.accounts!, account];
      return state;
    },
    resetUser: (): State => {
      return { ...initialState };
    },
    updateFacilitatedOrderAutoApprove: (state: State, action: PayloadAction<boolean>): State => {
      if (state.currentAccount && state.currentAccount.customer) {
        state.currentAccount.customer.approveFacilitatedOrdersFromCurrentPartnership = action.payload;
      }
      return state;
    },
    setTermsAccepted: (state: State, action: PayloadAction<TermsAndConditions>): State => {
      if (action.payload) {
        if (state.currentAccount?.customer?.settings) {
          state.currentAccount.customer.settings.termsAndConditions = action.payload;
        }
        if (state.currentAccount?.partnership?.settings) {
          state.currentAccount.partnership.settings.termsAndConditions = action.payload;
        }
      }
      return state;
    },

    setPrivacyAccepted: (state: State, action: PayloadAction<PrivacyPolicy>): State => {
      if (state.currentAccount?.settings) {
        state.currentAccount.settings.privacyPolicy = action.payload;
      } else if (state.currentAccount) {
        state.currentAccount.settings = {
          privacyPolicy: action.payload,
        };
      }
      return state;
    },

    setPartnerCustomers: (state: State, action: PayloadAction<PartnershipCustomers>): State => {
      state.currentAccount!.partnership!.partnershipCustomers = action.payload;
      return state;
    },
  },
});

const {
  setUser,
  setCustomer,
  setLoading,
  setCurrentAccount,
  setCurrentProfile,
  updateProfile,
  updatePaymentMethod,
  updateDefaultPaymentMethod,
  clearMetadata,
  setNewOwner,
  setEmailSubscription,
  resetUser,
  changeCustomer,
  changePartnership,
  clearSingleMetadata,
  updateFacilitatedOrderAutoApprove,
  updateCustomerAddress,
  updateCustomerLanguage,
  updatePartnershipPaymentMethod,
  updateAccounts,
  setTermsAccepted,
  setAvailablePaymentMethods,
  setPrivacyAccepted,
  setPartnerCustomers,
} = slice.actions;

export { setCurrentAccount, updatePaymentMethod, resetUser, setCurrentProfile, updateDefaultPaymentMethod, setAvailablePaymentMethods };
export default slice.reducer;

function getDefaultContext(accountTypes: AccountType[]): PortalContext {
  if (hasType(accountTypes, ['WeChooose'])) {
    return 'wechooose';
  } else if (hasType(accountTypes, ['WeChoooseCommmunity'])) {
    return 'connect';
  }

  return 'mychooose';
}
export const getUserInfo = (attemptedToCreateBillinginfo?: boolean): AppThunk => async (dispatch): Promise<void> => {
  dispatch(setLoading(true));

  try {
    let account: Account | undefined;
    const data = await createUserApi('mychooose').getInfo();
    const accountTypes = getTypes(data.accounts);
    let context = localStorage.getItem(PORTAL_CONTEXT_KEY) ?? (getDefaultContext(accountTypes) as PortalContext);
    const currentProfile = localStorage.getItem(PORTAL_SELECTED_PROFILE_KEY) ?? 'Person';

    if (!hasType(accountTypes, [contextToAccountType[context]])) {
      context = getDefaultContext(accountTypes);
    }

    const userApi = createUserApi(context as PortalContext);

    const settings = await userApi.getSettingsForUser();
    dispatch(setFeatures({ features: settings.connectFeatures ?? [], context: 'connect' }));
    dispatch(setFeatures({ features: settings.features, context: 'wechooose' }));
    dispatch(setLogo(settings.logoUrl));
    dispatch(setLanguages(settings.language ? settings.language : []));
    dispatch(setFeeDisplay(settings.feeDisplay));
    dispatch(setOffsetTypesWithoutSaf(settings.safFeatures ?? []));

    if (settings.partnershipId) {
      dispatch(setPartnershipId(settings.partnershipId));
    }

    if (context === 'wechooose') {
      account = data.accounts.find(acc => acc.type === 'WeChooose')!;
      const mychoooseAccount = data.accounts.find(acc => acc.type === 'MyChooose');
      const ability = defineAbilityFor(account);
      const customer = await userApi.getCustomer(account.customerId);
      account.customer = customer;
      const currentAccountIdx = data.accounts.findIndex(a => a.customerId === account?.customerId) as number;
      if (data.accounts[currentAccountIdx]) {
        data.accounts[currentAccountIdx].customer = customer;
      }
      dispatch(setContext('wechooose'));
      dispatch(loadAttributionForCustomer(account.customerId));
      dispatch(loadUserAttributionDataForWeChooose());
      dispatch(loadCustomerAttributionDataForMyChooose(mychoooseAccount?.customerId));
      dispatch(setPaymentMethod(account.customer?.payment?.defaultPaymentMethod));
      dispatch(setPaymentMethods(customer.payment?.paymentMethods ?? []));
      dispatch(setCurrentAccount(account));
      dispatch(getRoles());
      const paymentMethodTypes = settings.paymentMethodTypes;
      dispatch(setPaymentMethodTypes(paymentMethodTypes));
      dispatch(createPaymentMethods(customer, account, paymentMethodTypes, attemptedToCreateBillinginfo));
      if (currentProfile === 'Person' || ability.cannot('view', 'CompanySettings')) {
        dispatch(setCurrentProfile({ ...account.profile!, type: 'Person' }));
      } else {
        const companyProfile: Partial<Profile> = {
          name: account.customer?.name,
          type: 'Company',
        };
        dispatch(setCurrentProfile(companyProfile as Profile));
      }
      if (settings.multiplierSettings) {
        dispatch(setMultiplier(settings.multiplierSettings));
      }
    } else if (context === 'connect') {
      account = data.accounts.find(acc => acc.type === 'WeChoooseCommmunity')!;
      const partnership = await userApi.getPartnership(account.partnershipId ?? '');
      account.partnership = partnership;
      const currentAccountIdx = data.accounts.findIndex(a => a.partnershipId === account?.partnershipId) as number;
      if (data.accounts[currentAccountIdx]) {
        data.accounts[currentAccountIdx].partnership = partnership;
      }
      dispatch(setContext('connect'));
      dispatch(setCurrentAccount(account));
      dispatch(loadAttributionForPartnership(account.partnershipId ?? ''));
      dispatch(setCurrentProfile({ ...account.profile!, type: 'Person' }));
      dispatch(setPaymentMethodTypes(['Invoice']));
    } else if (context === 'mychooose') {
      account = data.accounts.find(acc => acc.type === 'MyChooose')!;
      const customer = await userApi.getCustomer(account.customerId);
      account.customer = customer;
      const currentAccountIdx = data.accounts.findIndex(a => a.customerId === account?.customerId) as number;
      if (data.accounts[currentAccountIdx]) {
        data.accounts[currentAccountIdx].customer = customer;
      }
      const giftCards = await userApi.getReceivedGiftcards(account.customerId);
      dispatch(setCurrentAccount(account));
      dispatch(setContext('mychooose'));
      dispatch(loadAttributionForUser());
      dispatch(saveGiftcards(giftCards));
      dispatch(loadCustomerAttributionDataForMyChooose(account.customerId));
      dispatch(setPaymentMethod(account.customer?.payment?.defaultPaymentMethod));
      dispatch(setPaymentMethods(customer.payment?.paymentMethods ?? []));
      dispatch(setCurrentProfile({ ...account.profile!, type: 'Person' }));
      dispatch(setPaymentMethodTypes(['Stripe']));
    }

    dispatch(setUser(data));
    dispatch(loadCountries());

    dispatch(setSubscriptions(account?.customer?.subscriptions ?? []));

    dispatch(
      setOnboarding(
        localStorage.getItem(onboardingLocalStorageNames[context as PortalContext])
          ? JSON.parse(localStorage.getItem(onboardingLocalStorageNames[context as PortalContext]) ?? 'true')
          : true,
      ),
    );

    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const modifyMetadata = (metadatas: CustomMetadata[], onSuccess: CallableFunction | null, onError: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  if (!metadatas.length) {
    return;
  }
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  try {
    let latestCustomer: Customer;
    for (const metadata of metadatas) {
      latestCustomer = await userApi.modifyMetadata(
        metadata.internalName,
        metadata.displayName,
        currentUser?.customerId ?? '',
        metadata.type,
        metadata.listValues,
        !!metadata.isMandatory,
      );
    }

    //@ts-ignore
    dispatch(setCustomer(latestCustomer));
    dispatch(setLoading(false));
    onSuccess && onSuccess();
  } catch (err) {
    dispatch(setLoading(false));
    onError();
  }
};

export const createMetadata = (metadatas: CustomMetadata[], onSuccess: CallableFunction | null, onError: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    let latestCustomer: Customer;
    for (const metadata of metadatas) {
      latestCustomer = await userApi.createMetadata(
        metadata.internalName,
        metadata.displayName,
        metadata.type,
        currentUser?.customerId ?? '',
        metadata.listValues,
        metadata.isMandatory ?? false,
      );
    }

    //@ts-ignore
    dispatch(setCustomer(latestCustomer));
    dispatch(setLoading(false));
    onSuccess && onSuccess();
  } catch (err) {
    dispatch(setLoading(false));
    onError();
  }
};

export const deleteMetadata = (onSuccess: CallableFunction, onError: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    for (const metadata of currentUser?.customer?.customMetadata ?? []) {
      await userApi.deleteMetadata(metadata.internalName, currentUser?.customerId ?? '');
    }

    dispatch(clearMetadata());
    dispatch(setLoading(false));
    onSuccess();
  } catch (err) {
    dispatch(setLoading(false));
    onError();
  }
};

export const deleteSingleMetadata = (
  internalName: string | string[],
  onSuccess: CallableFunction | null,
  onError: CallableFunction,
): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    if (Array.isArray(internalName)) {
      for (const metadata of internalName) {
        await userApi.deleteMetadata(metadata, currentUser?.customerId ?? '');
      }
    } else {
      await userApi.deleteMetadata(internalName, currentUser?.customerId ?? '');
    }

    dispatch(clearSingleMetadata(internalName));
    dispatch(setLoading(false));
    onSuccess && onSuccess();
  } catch (err) {
    dispatch(setLoading(false));
    onError();
  }
};

export const updateCustomMetadata = (
  metadataToModify: CustomMetadata[],
  metadataToCreate: CustomMetadata[],
  metadataToDelete: string[],
  onSuccess: CallableFunction | null,
  onError: CallableFunction,
): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    let latestCustomer: Customer;

    for (const metadata of metadataToDelete) {
      await userApi.deleteMetadata(metadata, currentUser?.customerId ?? '');
    }

    for (const metadata of metadataToCreate) {
      latestCustomer = await userApi.createMetadata(
        metadata.internalName,
        metadata.displayName,
        metadata.type,
        currentUser?.customerId ?? '',
        metadata.listValues,
        metadata.isMandatory ?? false,
      );
    }

    for (const metadata of metadataToModify) {
      latestCustomer = await userApi.modifyMetadata(
        metadata.internalName,
        metadata.displayName,
        currentUser?.customerId ?? '',
        metadata.type,
        metadata.listValues,
        !!metadata.isMandatory,
      );
    }

    //@ts-ignore
    dispatch(setCustomer(latestCustomer));
    dispatch(setLoading(false));
    onSuccess && onSuccess();
  } catch (err) {
    dispatch(setLoading(false));
    onError();
  }
};

export const switchUserBasedOnContext = (context: PortalContext): AppThunk => async (dispatch, getState): Promise<void> => {
  const { accounts } = getState().user;
  const userApi = createUserApi(context);
  if (!accounts) {
    return;
  }

  const settings = await userApi.getSettingsForUser();
  dispatch(setFeatures({ features: settings.connectFeatures ?? [], context: 'connect' }));
  dispatch(setFeatures({ features: settings.features, context: 'wechooose' }));
  dispatch(setLogo(settings.logoUrl));

  if (context === 'wechooose') {
    const account = accounts.find(acc => acc.type === 'WeChooose');
    const accountIndex = accounts.findIndex(acc => acc.type === 'WeChooose');
    const myChoooseAccount = accounts.find(acc => acc.type === 'MyChooose');
    const customer = await userApi.getCustomer(account?.customerId ?? '');
    const currentAccount = { ...account!, customer };
    const newAccounts = [...accounts];
    newAccounts[accountIndex] = currentAccount;
    dispatch(setCurrentAccount(currentAccount));
    dispatch(setSubscriptions(customer?.subscriptions ?? []));
    dispatch(loadAttributionForCustomer(account?.customerId));
    dispatch(loadUserAttributionDataForWeChooose());
    dispatch(loadCustomerAttributionDataForMyChooose(myChoooseAccount?.customerId));
    dispatch(setPaymentMethod(customer?.payment?.defaultPaymentMethod));
    dispatch(setPaymentMethods(customer.payment?.paymentMethods ?? []));
    dispatch(getRoles());
    dispatch(updateAccounts(newAccounts));
    dispatch(setCurrentProfile({ ...account!.profile!, type: 'Person' }));
    account?.customerId && dispatch(loadProjects(account));
    const paymentMethodTypes = settings.paymentMethodTypes;
    dispatch(setPaymentMethodTypes(paymentMethodTypes));
  } else if (context === 'connect') {
    const account = { ...accounts.find(acc => acc.type === 'WeChoooseCommmunity')! };
    const accountIndex = accounts.findIndex(acc => acc.type === 'WeChoooseCommmunity');
    const partnership = await userApi.getPartnership(account.partnershipId ?? '');
    const currentAccount = { ...account!, partnership };
    const newAccounts = [...accounts];
    newAccounts[accountIndex] = currentAccount;
    dispatch(setCurrentAccount({ ...account, partnership }));
    dispatch(loadAttributionForPartnership(account.partnershipId ?? ''));
    dispatch(setSubscriptions(account?.customer?.subscriptions ?? []));
    dispatch(setCurrentProfile({ ...account!.profile!, type: 'Person' }));
    account?.partnershipId && dispatch(loadProjects(account));
    dispatch(updateAccounts(newAccounts));
    dispatch(setPaymentMethodTypes(['Invoice']));
  } else if (context === 'mychooose') {
    const account = accounts.find(acc => acc.type === 'MyChooose');
    const customer = await userApi.getCustomer(account?.customerId ?? '');
    const giftCards = await userApi.getReceivedGiftcards(account?.customerId ?? '');
    dispatch(saveGiftcards(giftCards));
    dispatch(setCurrentAccount({ ...account!, customer }));
    dispatch(setSubscriptions(customer?.subscriptions ?? []));
    dispatch(loadAttributionForUser());
    dispatch(loadCustomerAttributionDataForMyChooose(account?.customerId));
    dispatch(setPaymentMethod(customer?.payment?.defaultPaymentMethod));
    dispatch(setCurrentProfile({ ...account!.profile!, type: 'Person' }));
    dispatch(setPaymentMethodTypes(['Stripe']));
  } else if (context === 'guest-mychooose') {
    const account = accounts.find(acc => acc.type === 'GuestMyChooose');
    dispatch(setCurrentAccount(account!));
    dispatch(setPaymentMethodTypes(['Stripe']));
  } else if (context === 'guest-wechooose') {
    const account = accounts.find(acc => acc.type === 'GuestWeChooose');
    dispatch(setCurrentAccount(account!));
  }

  dispatch(clearCertificates());
  dispatch(loadProjects());

  dispatch(
    setOnboarding(
      localStorage.getItem(onboardingLocalStorageNames[context])
        ? JSON.parse(localStorage.getItem(onboardingLocalStorageNames[context]) ?? 'true')
        : true,
    ),
  );

  localStorage.setItem(PORTAL_CONTEXT_KEY, context);
};

export const switchProfileTo = (profileType: ProfileType): AppThunk => (dispatch, getState): void => {
  const { currentAccount } = getState().user;
  if (!currentAccount) {
    return;
  }
  if (profileType === 'Person') {
    dispatch(setCurrentProfile({ ...currentAccount.profile!, type: 'Person' }));
  } else if (profileType === 'Company') {
    const companyProfile: Partial<Profile> = {
      name: currentAccount.customer?.name,
      type: 'Company',
    };
    dispatch(setCurrentProfile(companyProfile as Profile));
  }
  localStorage.setItem(PORTAL_SELECTED_PROFILE_KEY, profileType);
};

export const saveProfile = (
  profile: Partial<Omit<Profile, 'pictureUrl' | 'type'>>,
  onSuccess: CallableFunction,
  onError: CallableFunction,
): AppThunk => async (dispatch, getState): Promise<void> => {
  const { currentProfile, currentAccount } = getState().user;
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  let id;

  if (context === 'connect') {
    id = currentAccount?.partnershipId ?? '';
  } else {
    id = currentAccount?.customerId ?? '';
  }

  dispatch(setLoading(true));
  try {
    (Object.keys(profile) as Array<keyof typeof profile>).forEach(key => !profile[key] && delete profile[key]);

    for (const key of Object.keys(profile) as Array<keyof typeof profile>) {
      await userApi.modifyUserInformation(key, profile[key] as string, id);
    }

    dispatch(updateProfile({ ...currentProfile, ...profile } as Profile));
    dispatch(setLoading(false));
    onSuccess();
  } catch {
    dispatch(setLoading(false));
    onError();
  }
};

export const uploadAvatar = (avatar: File, onSuccess: CallableFunction, onError: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const { currentProfile, currentAccount } = getState().user;
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    let data;
    if (context === 'connect') {
      data = await userApi.modifyAvatarPartner(avatar, currentAccount?.partnershipId ?? '');
    } else {
      data = await userApi.modifyAvatarCustomer(avatar, currentAccount?.customerId ?? '');
    }
    dispatch(updateProfile({ ...currentProfile, pictureUrl: data.profile?.pictureUrl } as Profile));
    onSuccess();
  } catch {
    onError();
  }
};

export const removeAvatar = (onSuccess: CallableFunction, onError: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  try {
    const { currentProfile, currentAccount } = getState().user;
    const context = appContextSelector(getState());
    const userApi = createUserApi(context);
    let id;

    if (context === 'connect') {
      id = currentAccount?.partnershipId ?? '';
    } else {
      id = currentAccount?.customerId ?? '';
    }
    await userApi.deleteAvatar(id);
    dispatch(updateProfile({ ...currentProfile, pictureUrl: '' } as Profile));
    onSuccess();
  } catch {
    onError();
  }
};

export const deleteUser = (onSuccess: CallableFunction, onError: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  try {
    const { currentAccount, currentProfile } = getState().user;
    const context = appContextSelector(getState());
    const userApi = createUserApi(context);

    dispatch(setLoading(true));
    if (currentProfile?.type === 'Person' && currentAccount?.type === 'MyChooose') {
      const id = currentAccount?.customerId ?? '';
      await userApi.deleteCustomer(id);
    } else if (currentProfile?.type === 'Person' && currentAccount?.type === 'WeChooose') {
      const id = currentAccount?.id ?? '';
      await userApi.deleteUserAccount(id);
    } else {
      const id = currentAccount?.customerId ?? '';
      await userApi.deleteUserAccount(id);
    }

    dispatch(setLoading(false));
    onSuccess();
  } catch {
    dispatch(setLoading(false));
    onError();
  }
};

export const deleteCustomer = (onSuccess: CallableFunction, onError: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  try {
    const { currentAccount } = getState().user;
    const context = appContextSelector(getState());
    const userApi = createUserApi(context);
    const id = currentAccount?.customerId ?? '';

    dispatch(setLoading(true));

    await userApi.deleteCustomer(id);
    dispatch(setLoading(false));
    onSuccess();
  } catch {
    dispatch(setLoading(false));
    onError();
  }
};

export const transferOwnership = (id: string, onError: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  try {
    const { currentAccount } = getState().user;
    const context = appContextSelector(getState());
    const userApi = createUserApi(context);
    dispatch(setLoading(true));

    const newOwner = await userApi.transferOwnership(currentAccount?.customerId ?? '', id);
    dispatch(setNewOwner(newOwner as Owner));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
    onError();
  }
};

export const changeEmailSubscription = (values: Partial<MailNotifications>, shouldSetLoading = true): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  try {
    const { currentAccount } = getState().user;
    const context = appContextSelector(getState());
    const userApi = createUserApi(context);
    shouldSetLoading && dispatch(setLoading(true));

    const data = await userApi.modifyEmailSubscription(values, currentAccount?.customerId ?? '');
    dispatch(setEmailSubscription(data));
    shouldSetLoading && dispatch(setLoading(false));
  } catch {
    shouldSetLoading && dispatch(setLoading(false));
  }
};

export const saveBillingInformation = (
  newBillingInformation: DeepPartial<BillingInformation>,
  onSuccess?: CallableFunction,
  onError?: CallableFunction,
  stripePaymentMethod?: PaymentMethod,
): AppThunk => async (dispatch, getState): Promise<void> => {
  const { currentAccount } = getState().user;
  const paymentMethodTypes = getState().payments.paymentMethodTypes;
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  const paymentMethodApi = createPaymentMethodApi(context);
  const isInvoicePaymentMethodCreated = !!currentAccount?.customer?.payment?.paymentMethods.find(p => p.type === 'Invoice');
  const paymentMethod = currentAccount?.customer?.payment?.paymentMethods.find(p => p.type === 'Invoice') ?? ({} as PaymentMethod);
  const isStripePaymentMethodAvaialble = paymentMethodTypes === null || paymentMethodTypes?.includes('Stripe');
  const isInvoiceDefaultPaymentMethod =
    currentAccount?.customer?.payment?.defaultPaymentMethod?.type === 'Invoice' ||
    (paymentMethodTypes?.length === 1 && paymentMethodTypes?.includes('Invoice')) ||
    currentAccount?.customer?.payment?.invoiceOnly;

  dispatch(setLoading(true));
  try {
    let newPaymentMethod;
    if (!isInvoicePaymentMethodCreated) {
      const result = await paymentMethodApi.createInvoicePaymentMethod(newBillingInformation as BillingInformation, currentAccount!.customerId);
      newPaymentMethod = { ...result, weight: 1 };
    } else {
      await userApi.modifyBillingInformation(
        newBillingInformation as BillingInformation,
        paymentMethod.weight,
        currentAccount!.customerId,
        paymentMethod.id,
      );
      newPaymentMethod = { ...paymentMethod, type: 'Invoice', invoice: { ...(newBillingInformation as BillingInformation) } };
    }

    if (currentAccount?.customer?.payment?.paymentMethods) {
      if (isInvoicePaymentMethodCreated) {
        dispatch(updatePaymentMethod(newPaymentMethod));
        dispatch(updateInvoicePaymentMethod(newPaymentMethod));
      } else {
        dispatch(
          setAvailablePaymentMethods(
            isStripePaymentMethodAvaialble && stripePaymentMethod
              ? [...currentAccount?.customer?.payment?.paymentMethods, newPaymentMethod, stripePaymentMethod]
              : [...currentAccount?.customer?.payment?.paymentMethods, newPaymentMethod],
          ),
        );
        dispatch(
          setPaymentMethods(
            isStripePaymentMethodAvaialble && stripePaymentMethod
              ? [...currentAccount?.customer?.payment?.paymentMethods, newPaymentMethod, stripePaymentMethod]
              : [...currentAccount?.customer?.payment?.paymentMethods, newPaymentMethod],
          ),
        );
      }
    } else {
      dispatch(setAvailablePaymentMethods([newPaymentMethod]));
      dispatch(setPaymentMethods([newPaymentMethod]));
    }

    if (isInvoiceDefaultPaymentMethod) {
      dispatch(updateDefaultPaymentMethod(newPaymentMethod));
      dispatch(setPaymentMethod(newPaymentMethod));
    }

    onSuccess?.();
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    onError?.();
  }
};

// Wait max 15 seconds for backend to calculate attribution, it shouldn't take
// more than couple of seconds so user won't notice anything
const waitForAttribution = async (getAttribution: () => Promise<Attribution>): Promise<void> => {
  for (let i = 0; i < 30; i++) {
    try {
      const attribution = await getAttribution();
      if (attribution) {
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch {
      continue;
    }
  }
};

export const loadGuestUser = (inviteId: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const isGuestContext = isGuestContextSelector(getState());
  const isGuestCertificate = location.pathname.startsWith('/guest/certificate');
  const userApi = createUserApi(context);
  try {
    dispatch(setLoading(true));
    const user = await getInvitedUser(inviteId);
    const customer = await userApi.getCustomer(user.customerId);
    await waitForAttribution(() => userApi.getAttributionForCustomer(user.customerId));

    let accounts: Account[];

    const weChoooseAccount: Account = {
      customerId: customer.id,
      customer,
      auth0Id: 'guest',
      customerRoles: [],
      customerType: 'Enterprise',
      displayName: customer.name,
      effectiveCustomerPermissions: [],
      email: customer.email,
      emailVerified: true,
      id: customer.id,
      mailNotifications: null,
      rpDisplayName: customer.name,
      type: 'GuestWeChooose',
    };
    const myChoooseAccount: Account = {
      customerId: customer.id,
      customer,
      auth0Id: 'guest',
      customerRoles: [],
      customerType: 'Person',
      displayName: customer.name,
      effectiveCustomerPermissions: [],
      email: customer.email,
      emailVerified: true,
      id: customer.id,
      mailNotifications: null,
      rpDisplayName: customer.name,
      type: 'GuestMyChooose',
    };

    if (user.type === 'Gift') {
      accounts = [myChoooseAccount];
    } else if (customer.typeHint == null) {
      accounts = [myChoooseAccount, weChoooseAccount];
    } else if (customer.typeHint === 'Enterprise') {
      accounts = [weChoooseAccount];
    } else {
      accounts = [myChoooseAccount];
    }

    let targetContext: PortalContext;

    if (!isGuestContext && !isGuestCertificate && customer.typeHint == null) {
      targetContext = 'guest-mychooose';
      const giftCards = await userApi.getReceivedGiftcards(user.customerId);
      dispatch(saveGiftcards(giftCards));
    } else if (isGuestContext && !isGuestCertificate) {
      targetContext = context;
    } else {
      targetContext = 'guest-wechooose';
    }

    dispatch(
      setOnboarding(
        localStorage.getItem(onboardingLocalStorageNames[targetContext])
          ? JSON.parse(localStorage.getItem(onboardingLocalStorageNames[targetContext]) ?? 'true')
          : true,
      ),
    );

    dispatch(setContext(targetContext));
    dispatch(setCurrentAccount(accounts[0]));
    dispatch(
      setCurrentProfile({
        type: 'Person',
        name: customer.name,
      }),
    );
    dispatch(
      setUser({
        accounts: accounts,
        createdBy: null,
        createdDate: '',
        defaultAccount: accounts[0],
        id: 'userid',
        modifiedBy: '',
        modifiedDate: '',
      }),
    );

    dispatch(setSubscriptions(customer?.subscriptions ?? []));
    dispatch(loadAttributionForCustomer(customer.id));
    dispatch(setLoading(false));
  } catch {
    //err
  }
};

export const changePublicSetting = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const hasPublicAccess = hasPublicAccessSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    if (context === 'connect') {
      const partnership = await userApi.updatePartnership({
        id: currentAccount?.partnershipId ?? '',
        settings: {
          public: !hasPublicAccess,
        },
      });
      dispatch(changePartnership(partnership));
    } else {
      const customer = await userApi.updateCustomer({
        id: currentAccount?.customerId ?? '',
        settings: {
          public: !hasPublicAccess,
        },
      });

      dispatch(changeCustomer(customer));
    }
  } catch {
    //err
  }
};

export const updateBillingData = (
  newData: DeepPartial<UpdateCustomer>,
  onSuccess: CallableFunction,
  onError: CallableFunction,
  newBillingInformation?: DeepPartial<BillingInformation>,
): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  dispatch(setLoading(true));

  try {
    const customer = await userApi.updateCustomer(newData as UpdateCustomer);
    dispatch(changeCustomer(customer));
    if (newBillingInformation) {
      dispatch(saveBillingInformation(newBillingInformation, onSuccess, onError));
    } else {
      dispatch(setLoading(false));
      onSuccess();
    }
  } catch (err) {
    dispatch(setLoading(false));
    onError();
  }
};

export const updatePartnershipInvoiceData = (
  newData: DeepPartial<BillingInformation>,
  onSuccess: CallableFunction,
  onError: CallableFunction,
): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  const paymentMethodApi = createPaymentMethodApi(context);
  const { currentAccount } = getState().user;
  const paymentMethod = currentAccount?.partnership?.settings?.invoice;
  dispatch(setLoading(true));
  try {
    paymentMethod
      ? await userApi.updatePartnershipInvoiceInformation(newData as BillingInformation, currentAccount?.partnershipId ?? '')
      : await paymentMethodApi.createInvoicePaymentMethodForPartnership(newData as BillingInformation, currentAccount?.partnershipId ?? '');
    dispatch(updatePartnershipPaymentMethod({ ...paymentMethod, ...(newData as BillingInformation) }));
    dispatch(setLoading(false));
    onSuccess();
  } catch (err) {
    dispatch(setLoading(false));
    onError();
  }
};

export const updateCustomerData = (newData: CustomerInformation, onSuccess?: CallableFunction, onError?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  const currentAccount = currentUserSelector(getState());
  const customerId = currentAccount?.customerId ?? '';
  dispatch(setLoading(true));

  try {
    const result = await userApi.updateCustomerInformation(newData, customerId);
    dispatch(updateCustomerAddress({ id: customerId, customerInformation: result }));
    dispatch(setLoading(false));
    onSuccess && onSuccess();
  } catch (err) {
    dispatch(setLoading(false));
    onError && onError();
  }
};

export const updateLanguage = (language: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const context = appContextSelector(getState());
  const currentAccount = currentUserSelector(getState());
  const userApi = createUserApi(context);
  const isConnectContext = context === 'connect';

  let id;
  if (isConnectContext) {
    id = currentAccount?.partnershipId ?? '';
  } else {
    id = currentAccount?.customerId ?? '';
  }

  dispatch(setLoading(true));

  try {
    await userApi.modifyUserInformation('language', language, id);
    dispatch(updateCustomerLanguage({ language }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    //err
  }
};

export const updateAutoApprove = (autoApprove: boolean, onError: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    await userApi.updateFacilitatedOrdersAutoApprove(currentAccount?.customerId ?? '', autoApprove);
    dispatch(updateFacilitatedOrderAutoApprove(autoApprove));
  } catch (err) {
    onError(err);
  }
};

const createPaymentMethods = (
  customer: Customer,
  account: Account,
  paymentMethodTypes: PaymentMethodTypes,
  attemptedToCreateBillinginfo: boolean | undefined,
): AppThunk => async (dispatch, getState): Promise<void> => {
  if (attemptedToCreateBillinginfo) {
    return;
  }
  const context = appContextSelector(getState());

  const isInvoiceDefaultPaymentMethod =
    customer.payment?.defaultPaymentMethod?.type === 'Invoice' ||
    (!customer.payment?.defaultPaymentMethod && paymentMethodTypes?.length === 1 && paymentMethodTypes?.includes('Invoice'));
  const isInvoicePaymentMethodAvailable = paymentMethodTypes === null || paymentMethodTypes?.includes('Invoice');
  const isStripePaymentMethodAvailable = paymentMethodTypes === null || paymentMethodTypes?.includes('Stripe');

  let stripePaymentMethod: undefined | PaymentMethod;

  if (isStripePaymentMethodAvailable && !account.customer?.payment?.paymentMethods.find(({ type }) => type === 'Stripe')) {
    try {
      const paymentMethodApi = createPaymentMethodApi(context);
      const result = await paymentMethodApi.createStripePaymentMethod(account.customerId, isInvoicePaymentMethodAvailable ? 2 : 1);
      stripePaymentMethod = { ...result, status: 'Incomplete' };
      if (!isInvoiceDefaultPaymentMethod) {
        dispatch(setPaymentMethod(stripePaymentMethod));
        dispatch(updateDefaultPaymentMethod(stripePaymentMethod));
      }
      if (!isInvoicePaymentMethodAvailable) {
        dispatch(setAvailablePaymentMethods([stripePaymentMethod]));
        dispatch(setPaymentMethods([stripePaymentMethod]));
      }
    } catch {
      //err
    }
  }

  if (isInvoicePaymentMethodAvailable && !account.customer?.payment?.paymentMethods.find(({ type }) => type === 'Invoice')?.invoice) {
    const initialBillingInformation: BillingInformationRequest = {
      contact: {
        email: account?.customer?.owner?.email,
        name: undefined,
        phoneNumber: undefined,
      },
      billingEmail: account?.customer?.owner?.email,
      confirmed: false,
    };
    if (customer.info?.address?.line1 && customer.info?.address?.city && customer.info?.address?.countryCodeIso2) {
      initialBillingInformation.address = {
        line1: customer.info?.address?.line1,
        line2: customer.info?.address?.line2,
        zipCode: customer.info?.address?.zipCode,
        city: customer.info?.address?.city,
        countryCodeIso2: customer.info?.address?.countryCodeIso2,
        state: customer.info?.address?.state,
      };
    }
    dispatch(saveBillingInformation(initialBillingInformation, undefined, undefined, stripePaymentMethod));
  }
};

export const acceptTermsAndConditions = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    let response;
    if (context === 'connect') {
      response = await userApi.acceptTermsPartnership(currentAccount?.partnershipId ?? '');
    } else {
      response = await userApi.acceptTermsCustomer(currentAccount?.customerId ?? '');
    }
    const privacyResponse = await userApi.acceptPrivacyPolicy(currentAccount?.id ?? '');
    dispatch(setPrivacyAccepted(privacyResponse.privacyPolicy));
    dispatch(setTermsAccepted(response.termsAndConditions));
  } catch {
    // err
  }
};

export const acceptPersonalTerms = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentAccount = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);

  try {
    const res = await userApi.acceptPrivacyPolicy(currentAccount?.id ?? '');
    dispatch(setPrivacyAccepted(res.privacyPolicy));
  } catch {
    // err
  }
};

export const getChannelGroupName = (group: string): string => group.replaceAll('wechooose', 'portal');

export const getPartnerCustomers = (partnershipId: string, query: string, limit: number, offset: number): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const userApi = createUserApi(context);
  try {
    const result = await userApi.getPartnersCustomers(partnershipId, query, limit, offset);
    dispatch(
      setPartnerCustomers({
        items: result.items,
        totalCount: result.totalCount,
      }),
    );
  } catch {
    //  error
  } finally {
    dispatch(setLoading(false));
  }
};

export const transactionsPerChannelForCurrentWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisWeek?.countPerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2PerChannelForCurrentWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisWeek?.co2PerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsPerChannelForLastWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastWeek?.countPerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2PerChannelForLastWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastWeek?.co2PerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsPerChannelForCurrentMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisMonth?.countPerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2PerChannelForCurrentMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisMonth?.co2PerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsPerChannelForLastMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastMonth?.countPerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2PerChannelForLastMonth = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastMonth?.co2PerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsPerChannelForCurrentYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisYear?.countPerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2PerChannelForCurrentYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisYear?.co2PerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsPerChannelForLastYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastYear?.countPerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2PerChannelForLastYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastYear?.co2PerChannel;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsOverTimeForCurrentWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisWeek?.countPerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2OverTimeForCurrentWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisWeek?.co2PerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsOverTimeForLastWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastWeek?.countPerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2OverTimeForLastWeek = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastWeek?.co2PerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsOverTimeForCurrentQuarter = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisQuarter?.countPerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2OverTimeForCurrentQuarter = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisQuarter?.co2PerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsOverTimeForLastQuarter = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastQuarter?.countPerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2OverTimeForLastQuarter = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastQuarter?.co2PerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsOverTimeForCurrentYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisYear?.countPerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2OverTimeForCurrentYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.thisYear?.co2PerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const transactionsOverTimeForLastYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastYear?.countPerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.count } : { name: getChannelGroupName(channel.group), value: channel.count },
    ),
);

export const transactionsCO2OverTimeForLastYear = createSelector(
  (state: RootState) => {
    const attribution = state.attribution;
    if (attribution && attribution.statistics) {
      return attribution.statistics.facilitated?.lastYear?.co2PerTimeUnit;
    }
    return [];
  },
  channels =>
    channels?.map(channel =>
      !channel.group ? { name: 'Other', value: channel.kilosCo2 } : { name: getChannelGroupName(channel.group), value: channel.kilosCo2 },
    ),
);

export const currentUserSelector = (state: RootState): Account | undefined => state.user.currentAccount;

export const powerBiReportIdSelector = (state: RootState): string | undefined =>
  // eslint-disable-next-line no-undef
  state.user.currentAccount?.partnership?.settings?.powerBi?.reportId ?? process.env.REACT_APP_DEFAULT_ANALYTICS_POWERBI_REPORT;

export const customerOwnerSelector = (state: RootState): Owner | undefined => state.user.currentAccount?.customer?.owner;

export const isCurrentUserOwner = (state: RootState): boolean => {
  const id = state.user.currentAccount?.customer?.owner?.userAccountId;
  return id ? id === state.user.currentAccount?.id : false;
};

export const currentUserRoleIdSelector = (state: RootState): string | undefined => {
  if (state.user.currentAccount && state.user.currentAccount.customerRoles.length) {
    return state.user.currentAccount.customerRoles[0].customerRoleId;
  }

  return undefined;
};

export const isTermsAcceptedSelector = createSelector(
  (state: RootState) => currentUserSelector(state),
  currentUser => currentUser?.customer?.settings?.termsAndConditions?.accepted ?? currentUser?.partnership?.settings?.termsAndConditions?.accepted,
);

export const hasPublicAccessSelector = createSelector(
  (state: RootState) => currentUserSelector(state),
  (state: RootState) => appContextSelector(state),
  (currentUser, context) => {
    if (context === 'wechooose') {
      return currentUser?.customer?.settings?.public ?? false;
    }
    if (context === 'connect') {
      return currentUser?.partnership?.settings?.public ?? false;
    }

    return false;
  },
);

export const currentUserCountryCodeSelector = (state: RootState): string | undefined =>
  state.user.currentAccount?.customer?.info?.address?.countryCodeIso2;

export const itemFeeSelector = createSelector(
  (state: RootState) => state.user.currentAccount,
  appContextSelector,
  (account, context) => {
    if (context === 'connect') {
      return account?.partnership?.settings?.customerFees?.itemFee;
    }

    return account?.customer?.fees.itemFee;
  },
);

export const feeCurrencySelector = (state: RootState): string | undefined => state.user.currentAccount?.customer?.fees.feeCurrency;

export const currentUserOffsetFromDate = createSelector(currentUserSelector, account => account?.customer?.owner?.employeeOffsetFromDate ?? '');

export const isEmployeeOffset = createSelector(currentUserSelector, account => account?.customer?.owner?.employeeOffset);

export const isMigratedUser = (state: RootState): boolean | undefined => state.user.currentAccount?.customer?.state?.isMigrated;

export const currentProfileSelector = (state: RootState): Profile | undefined => state.user.currentProfile;

export const metadatasSelector = createSelector(
  (state: RootState) => state.user,
  user => user.currentAccount?.customer?.customMetadata,
);

export const customMetadataSelector = createSelector(
  (state: RootState) => state.user,
  user => user.currentAccount?.customer?.customMetadata.filter(({ internalName }) => !preDefinedMetadatasNames.includes(internalName)),
);

export const isPrivacyPolicyAcceptedSelector = createSelector(
  (state: RootState) => state.user,
  user => user.currentAccount?.settings?.privacyPolicy?.accepted,
);

export const isRequiredBillingInformationMissingSelector = createSelector(
  (state: RootState) => state.user.currentAccount?.customer?.info?.address,
  (state: RootState) => state.app.context,
  (address, context) => {
    const isMissingStateInfo = address?.countryCodeIso2 === 'CA' && !address?.state;
    return context === 'wechooose' ? !address?.countryCodeIso2 || isMissingStateInfo : false;
  },
);
